import { useState, useEffect, useCallback } from 'react';
import { ChevronDown, Filter, PlusCircle } from 'lucide-react';
import PropertyCard from '../components/PropertyCard';
import AddPropertyDrawer from '../components/AddPropertyDrawer';
import { getProperties } from '../api/properties';
import { supabase } from '../supabaseClient';
import { fetchUsageSafe } from '../api/usage';

const PROPERTIES_PER_PAGE = 9;

export default function Properties() {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  // NEW: filter + sort state
  const [filterType, setFilterType] = useState(''); // e.g. 'house', ''
  const [filterStatus, setFilterStatus] = useState(''); // e.g. 'vacant', ''
  const [sortBy, setSortBy] = useState(''); // 'az' | 'za' | 'latest' | 'earliest' | ''

  const [usage, setUsage] = useState(null);
  const propertyLimitReached =
    Number.isFinite(usage?.propertyLimit) &&
    usage.propertyCount >= usage.propertyLimit;

  useEffect(() => {
    let alive = true;

    fetchUsageSafe().then((data) => {
      if (alive) setUsage(data);
    });

    return () => {
      alive = false;
    };
  }, []);

  // central fetch function (parent owns canonical data)
  const fetchProperties = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getProperties();
      const raw = res?.data ?? [];

      const propertiesWithImageUrls = raw.map((property) => {
        let imageUrl = property.imageUrl || null;
        if (!imageUrl && property.image) {
          try {
            const { data, error } = supabase.storage
              .from('property-images')
              .getPublicUrl(property.image);
            imageUrl = error ? null : data?.publicUrl ?? null;
          } catch (err) {
            imageUrl = null;
          }
        }
        return { ...property, imageUrl };
      });

      setProperties(propertiesWithImageUrls);
    } catch (err) {
      console.error('Failed to fetch properties:', err);
      setProperties([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // fetch once on mount
  useEffect(() => {
    fetchProperties();
  }, [fetchProperties]);

  // parent callback to refetch when a property is created
  const handlePropertyCreated = useCallback(
    async (created) => {
      console.log('[Properties] handlePropertyCreated called ->', created);
      try {
        await fetchProperties();
        setPage(1);
      } catch (err) {
        console.error('[Properties] error refetching after create', err);
      }
    },
    [fetchProperties]
  );

  // FILTERING + SORTING PIPELINE
  const filteredProperties = properties
    // 1) text search (title)
    .filter((property) => {
      if (!search) return true;
      return property.title?.toLowerCase().includes(search.toLowerCase());
    })
    // 2) type filter
    .filter((property) => {
      if (!filterType) return true;
      // Accept both `type` and `property_type` common naming
      const t = (property.type ?? property.property_type ?? '').toString().toLowerCase();
      return t === filterType.toLowerCase();
    })
    // 3) status filter
    .filter((property) => {
      if (!filterStatus) return true;
      const s = (property.status ?? '').toString().toLowerCase();
      return s === filterStatus.toLowerCase();
    })
    // 4) sorting
    .sort((a, b) => {
      if (!sortBy || sortBy === '') return 0;

      // Helper for title compare
      const titleA = (a.title ?? '').toString().toLowerCase();
      const titleB = (b.title ?? '').toString().toLowerCase();

      if (sortBy === 'az') {
        if (titleA < titleB) return -1;
        if (titleA > titleB) return 1;
        return 0;
      }

      if (sortBy === 'za') {
        if (titleA < titleB) return 1;
        if (titleA > titleB) return -1;
        return 0;
      }

      // For date sorting try created_at, then fallback to title
      const parseDate = (p) => {
        const d = p?.created_at ?? p?.createdAt ?? p?.created ?? null;
        const parsed = d ? Date.parse(d) : NaN;
        return Number.isFinite(parsed) ? parsed : null;
      };

      const dateA = parseDate(a);
      const dateB = parseDate(b);

      if ((sortBy === 'latest' || sortBy === 'earliest') && dateA !== null && dateB !== null) {
        if (sortBy === 'latest') return dateB - dateA; // newest first
        return dateA - dateB; // oldest first
      }

      // If date missing or unparsable, fall back to title ordering
      if (titleA < titleB) return sortBy === 'latest' ? -1 : -1;
      if (titleA > titleB) return sortBy === 'latest' ? 1 : 1;
      return 0;
    });

  // pagination uses filteredProperties
  const filteredTotalPages = Math.max(1, Math.ceil(filteredProperties.length / PROPERTIES_PER_PAGE));

  const paginatedProperties = filteredProperties.slice(
    (page - 1) * PROPERTIES_PER_PAGE,
    page * PROPERTIES_PER_PAGE
  );

  return (
    <div className="pt-10 md:pt-0">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-4xl">Your properties</h1>
        <button
          onClick={() => setIsDrawerOpen(true)}
          disabled={propertyLimitReached}
          className={`flex items-center gap-2 px-4 py-2 border rounded-xl transition backdrop-blur-md
            ${
              propertyLimitReached
                ? 'bg-white/5 text-white/40 border-white/5 cursor-not-allowed'
                : 'bg-white/10 text-white border-white/10 hover:bg-[#AF9B8A]/20'
            }`}
        >
          <PlusCircle className="w-5 h-5" />
          <span>New Property</span>
        </button>
      </div>

      <div className="h-px w-36 bg-[#AF9B8A] rounded-full mb-6"></div>

      {/* Filters */}
      <div className="mb-6 flex flex-wrap gap-4 items-center">
        <input
          type="text"
          placeholder="Search by name..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          className="px-4 py-2 rounded-lg bg-white/10 border border-white/10 placeholder-gray-400 text-white backdrop-blur-md"
        />

        {/* Property Type select */}
        <div className="relative">
          <select
            value={filterType}
            onChange={(e) => {
              setFilterType(e.target.value);
              setPage(1);
            }}
            className="appearance-none w-48 p-3 rounded-lg text-white bg-white/5 border border-white/10 focus:outline-none focus:ring-2 focus:ring-[#AF9B8A] glass"
            style={{ cursor: 'pointer' }}
          >
            <option value="">Property Type</option>
            <option value="house">House</option>
            <option value="flat">Flat</option>
            <option value="bungalow">Bungalow</option>
            <option value="hmo">HMO</option>
            <option value="commercial">Commercial</option>
            <option value="mixed-use">Mixed Use</option>
          </select>
          <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none text-white/70" />
        </div>

        {/* Status select */}
        <div className="relative">
          <select
            value={filterStatus}
            onChange={(e) => {
              setFilterStatus(e.target.value);
              setPage(1);
            }}
            className="appearance-none w-48 p-3 rounded-lg text-white bg-white/5 border border-white/10 focus:outline-none focus:ring-2 focus:ring-[#AF9B8A] glass"
            style={{ cursor: 'pointer' }}
          >
            <option value="">All Properties</option>
            <option value="vacant">Vacant</option>
            <option value="occupied">Occupied</option>
          </select>
          <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none text-white/70" />
        </div>

        {/* Sort select */}
        <div className="relative">
          <select
            value={sortBy}
            onChange={(e) => {
              setSortBy(e.target.value);
              setPage(1);
            }}
            className="appearance-none w-48 p-3 rounded-lg text-white bg-white/5 border border-white/10 focus:outline-none focus:ring-2 focus:ring-[#AF9B8A] glass"
            style={{ cursor: 'pointer' }}
          >
            <option value="">Sort</option>
            <option value="az">A - Z</option>
            <option value="za">Z - A</option>
            <option value="latest">Date Created: Latest</option>
            <option value="earliest">Date Created: Earliest</option>
          </select>
          <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none text-white/70" />
        </div>

        <button className="flex items-center gap-2 px-4 py-2 bg-[#AF9B8A]/20 text-white border border-white/10 rounded-xl hover:bg-[#AF9B8A]/40 transition backdrop-blur-md">
          <Filter className="w-4 h-4" />
          <span>Apply Filters</span>
        </button>
      </div>

      {/* Loading */}
      {loading ? (
        <div className="text-white opacity-70 text-center py-12">Loading your properties...</div>
      ) : (
        <>
          {/* Property Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 mb-8">
            {paginatedProperties.map((property) => (
              <PropertyCard key={property.id} property={property} />
            ))}
            {paginatedProperties.length === 0 && (
              <div className="col-span-full text-center text-white opacity-60 py-12">
                No properties found.
              </div>
            )}
          </div>

          {/* Pagination */}
          <div className="flex justify-center items-center gap-2 mb-8">
            <button
              className="px-4 py-2 rounded-lg bg-white/10 text-white border border-white/10 hover:bg-[#AF9B8A]/20 transition backdrop-blur-md"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              Previous Page
            </button>
            {Array.from({ length: filteredTotalPages }, (_, i) => (
              <button
                key={i + 1}
                className={`px-3 py-2 rounded-lg border ${
                  page === i + 1
                    ? 'bg-[#AF9B8A] text-white'
                    : 'bg-white/10 text-white border-white/10'
                } hover:bg-[#AF9B8A]/40 transition`}
                onClick={() => setPage(i + 1)}
              >
                {i + 1}
              </button>
            ))}
            <button
              className="px-4 py-2 rounded-lg bg-white/10 text-white border border-white/10 hover:bg-[#AF9B8A]/20 transition backdrop-blur-md"
              onClick={() => setPage((p) => Math.min(filteredTotalPages, p + 1))}
              disabled={page === filteredTotalPages}
            >
              Next Page
            </button>
          </div>
        </>
      )}

      {/* Flyout Drawer - pass onCreate so parent can refetch */}
      <AddPropertyDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        onCreate={handlePropertyCreated}
      />
    </div>
  );
}
